
import { sub } from "date-fns/esm"
import { useEffect, useRef, useState } from "react"
import { pollingIntervals } from "../constants/pollingIntervals"
import { LocalStorageCache } from "../utils/cache"
import { useInterval } from "./useInterval"
import { add } from "date-fns"
import { IWebClient } from "@cd2/skeem-client"

/**
 * Locks prevent editing (softly) to given resources.
 *
 * A user claims a lock on a resource by calling the useLock(resource, id) hook. This will create a
 * lock record for the specified resource/id and user. The lock has a lockedAt date specifying when
 * the lock was claimed. When the hook unmounts the lock is released.
 *
 * To check what locks are on a resource the useLockData(resource) hook is used. This hook will
 * fetch the data about what locks are held for the given resource. If a locks lockedAt date is too
 * old then the data is ignored.
 */

// how long should a claimed lock last for
const LOCK_VALID_FOR = 30
// once a lock is obtained, how often should you refresh the locks timeout
const LOCK_REFRESH_TIME = pollingIntervals.lockRefreshTime
// how often should you check for new lock data
const LOCK_CHECK_TIME = pollingIntervals.lockCheckTime

// claim a lock for a specified resource and id
// this lock will last for LOCK_VALID_FOR and be refreshed every LOCK_REFRESH_TIME
// when the hook unmounts the lock is released immediately

export async function createLock(skeem: IWebClient, resource: string, id: string) {
  const hasLockAlready = await skeem.fetch2({
    first: {
      name: "resourceLocks",
      filter: {
        and: [
          { matches: { resourceId: id, resourceName: resource } },
          { not: { matches: { user: { session: true } } } },
          {
            gte: [
              { attr: "lockedAt" },
              { value: sub(new Date(), { seconds: LOCK_VALID_FOR + 10 }) },
            ],
          },
        ],
      },
    },
  })
  if (hasLockAlready) {
    console.warn("Cannot obtain lock for", resource, id, "as one already exists")
    return false
  }
  const lock = await skeem.mutate<{ resourceName: string }>({
    resourceLocks: {
      create: {
        resourceName: resource,
        resourceId: id,
        user: { set: { session: true } },
      },
    },
  })
  return lock
}

export function useLock(resource: string, id?: string) {
  const skeem = window.skeem

  // we store a reference to the obtained lock, that way we can delete it immediately on unmount
  // or resource change. It also means we update the lock time when refreshing instead of creating
  // a new record
  const lock = useRef<{ id: string; resourceName: string }>()

  // only one acquire/refresh runs at a time, otherwise the first interval tick can race the
  // initial claim and create a second lock row that nothing ever releases
  const inflight = useRef<Promise<void>>()
  // bumped whenever the lock is released, so results from acquisitions started before that are
  // destroyed rather than stored
  const generation = useRef(0)

  function refreshLock() {
    if (inflight.current) {
      return inflight.current
    }
    const gen = generation.current
    const promise: Promise<void> = (async () => {
      const obtained = await obtainLock(resource)
      if (!obtained) {
        return
      }
      if (gen !== generation.current) {
        skeem.mutate({ resourceLocks: { destroy: obtained.id } })
        return
      }
      lock.current = obtained
    })().finally(() => {
      if (inflight.current === promise) {
        inflight.current = undefined
      }
    })
    inflight.current = promise
    return promise
  }

  // useInterval keeps the first callback it was given, so go via a ref to see the current id
  const latestRefresh = useRef(refreshLock)
  latestRefresh.current = refreshLock
  useInterval(() => latestRefresh.current(), LOCK_REFRESH_TIME)

  useEffect(() => {
    refreshLock()

    const release = () => {
      generation.current += 1
      inflight.current = undefined
      if (lock.current) {
        skeem.mutate({ resourceLocks: { destroy: lock.current.id } })
        lock.current = undefined
      }
    }
    // unmount doesn't run when the tab is closed or navigated away, so release on pagehide too
    window.addEventListener("pagehide", release)
    return () => {
      window.removeEventListener("pagehide", release)
      release()
    }
  }, [resource, id])

  async function obtainLock(resource: string) {
    if (!id) {
      return
    }
    if (lock.current) {
      await skeem.mutate<{ resourceName: string }>({
        resourceLocks: {
          update: {
            id: lock.current.id,
            lockedAt: new Date(),
          },
        },
      })
      return { ...lock.current, lockedAt: new Date().toISOString() }
    } else {
      const lock = await createLock(skeem, resource, id)
      if (lock) {
        return lock
      }
    }
  }
}

export type ILockData = {
  resourceId: string
  userId: string
  userName: string
  lockedAt: string
}

const lockDataCache = new LocalStorageCache<ILockData[]>({ 
  namespace: "lockDataCache", 
  timeoutSeconds: 5 
})

// Track in-flight requests to prevent duplicate fetches
const inflightRequests = new Map<string, Promise<ILockData[]>>()

// pass an id to only fetch the locks for that one record rather than every lock on the resource
export function useLockData(resource: string, pollingInterval?: number, id?: string) {
  const skeem = window.skeem
  const [currentLocks, setCurrentLocks] = useState<null | ILockData[]>(null)

  const cacheKey = id ? `${resource}:${id}` : resource

  useInterval(() => updateLocks(resource, cacheKey), pollingInterval ?? LOCK_CHECK_TIME)
  useEffect(() => {
    setCurrentLocks(null)
    updateLocks(resource, cacheKey)
  }, [cacheKey])

  async function updateLocks(resource: string, cacheKey: string) {
    // Check cache first
    const cached = lockDataCache.get(cacheKey)
    if (cached) {
      setCurrentLocks(cached)
      return
    }

    // Check if there's already a request in flight for this resource
    const inflight = inflightRequests.get(cacheKey)
    if (inflight) {
      const locks = await inflight
      setCurrentLocks(locks)
      return
    }

    // Create new request
    const promise = (async () => {
      const earliestValidLockTime = sub(new Date(), { seconds: LOCK_VALID_FOR })

      // fetch all valid locks: for the given resource, not owned by us, lockedAt date is in range
      const currentLocks = await skeem.fetch<{
        resourceId: string
        user: { id: string; name: string }
        lockedAt: string
      }>({
        resourceLocks: {
          attributes: [{ name: "user", attributes: ["name"] }, "resourceId", "lockedAt"],
          filter: {
            and: [
              { matches: id ? { resourceName: resource, resourceId: id } : { resourceName: resource } },
              { not: { eq: [{ attr: "user" }, { session: true }] } },
              { gt: [{ attr: "lockedAt" }, { value: earliestValidLockTime }] },
            ],
          },
        },
      })

      const formatted = currentLocks.map(lock => ({
        resourceId: lock.resourceId,
        lockedAt: lock.lockedAt,
        userId: lock.user.id,
        userName: lock.user.name,
      }))

      // Cache the resolved data
      lockDataCache.set(cacheKey, formatted)
      
      // Clean up in-flight tracker
      inflightRequests.delete(cacheKey)
      
      return formatted
    })()

    // Track the in-flight request
    inflightRequests.set(cacheKey, promise)

    const locks = await promise
    setCurrentLocks(locks)
  }

  return currentLocks
}


export function useLocked(resource: string, id?: string, pollingInterval?: number) {
  const locks = useLockData(resource, pollingInterval, id)
  return locks?.find(lock => lock.resourceId === id)
}

